import type { ReactElement } from 'react';
import { ListTree } from 'lucide-react';
import type { GroupedAlertSessionLink } from '@kirin/api-client/contracts/agent-sessions';
import { PRESSED_ACTION_BUTTON_CLASS } from '@/components/alertActionStyles';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useIsOpenSession,
  useOpenSessionView,
  useSessionViewEnabled,
} from './session-view-link';

const SESSION_VIEW_BUTTON_HINT =
  'See every alert raised in this agent session, across policies, in the order the events happened.';

export interface SessionViewButtonProps {
  /** The occurrence the Session view opens from, and its session. */
  link: GroupedAlertSessionLink | undefined;
  /** Omitted / null → personal-account scope. */
  organizationId?: string | null;
}

/**
 * KIR-4785: the alert panel's entry to the Session view, beside Session
 * Timeline. Renders nothing unless both flags are on, the alert carries a
 * session link, and that session is not the Session view already open.
 */
export function SessionViewButton({
  link,
  organizationId,
}: SessionViewButtonProps): ReactElement | null {
  const enabled = useSessionViewEnabled();
  const isOpenSession = useIsOpenSession(link?.sessionId);
  if (!enabled || !link || isOpenSession) return null;
  return (
    <SessionViewButtonContent
      organizationId={organizationId ?? null}
      occurrenceId={link.occurrenceId}
    />
  );
}

/** Mounted only behind the flags: `useOpenSessionView` needs a router. */
function SessionViewButtonContent({
  organizationId,
  occurrenceId,
}: {
  organizationId: string | null;
  occurrenceId: string;
}): ReactElement {
  const openSessionView = useOpenSessionView(organizationId);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={PRESSED_ACTION_BUTTON_CLASS}
          onClick={() => openSessionView(occurrenceId)}
          data-testid="session-view-button"
        >
          <ListTree className="size-4" aria-hidden />
          View all alerts in this session
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        {SESSION_VIEW_BUTTON_HINT}
      </TooltipContent>
    </Tooltip>
  );
}

SessionViewButton.displayName = 'SessionViewButton';
